// Unlike the Google Tag Manager, the Posthog Client does not have a built-in way to

import type { CaptureOptions, EventName, Properties } from "posthog-js"
import { getCart } from "src/components/checkout/hooks/useCart"
import { CheckoutStep } from "src/components/checkout/state/checkoutStageStore"
import { withPosthogWeb } from "src/integrations/posthog/client-web"
import { posthogDebug } from "src/integrations/posthog/debug"
import { mapToPosthogLineItem, mapToPosthogOrder } from "src/integrations/posthog/mapping"
import { withPosthogFeatureFlags } from "src/integrations/posthog/useExperiment"
import { TrackingLineItem, TrackingOrder } from "src/tracking/types"

export const posthogCapture = (eventName: EventName, properties?: Properties | null, options?: CaptureOptions) => {
  // Avoid tracking events before feature flags loaded as it reduces our event
  // volume with feature flags set making A/B tests take longer.
  withPosthogWeb((posthog) => {
    withPosthogFeatureFlags(() => {
      posthogDebug(eventName, properties, options)
      posthog.capture(eventName, properties, options)
    })
  })
}

// =====================================
// Built-In Events
// https://posthog.com/docs/product-analytics/autocapture#autocaptured-events
// =====================================

// https://posthog.com/docs/libraries/next-js#capturing-pageviews
export function posthogTrackPageView(data: { referrer: string; pathname: string }) {
  posthogCapture("$pageview", {
    ...data,
    $current_url: window.location.href,
  })
}

// =====================================
// Ecommerce Events
// https://posthog.com/docs/data/event-spec/ecommerce-events
// =====================================

// https://posthog.com/docs/data/event-spec/ecommerce-events#products-searched
export function posthogTrackProductsSearched(data: { input: string; listId: string }) {
  posthogCapture("Products Searched", {
    query: data.input,

    // custom
    list_id: data.listId,
  })
}

// https://posthog.com/docs/data/event-spec/ecommerce-events#product-added
export function posthogTrackProductAdded(item: TrackingLineItem) {
  posthogCapture("Product Added", mapToPosthogLineItem(item))
}

// https://posthog.com/docs/data/event-spec/ecommerce-events#coupon-applied
export function posthogTrackCouponApplied(data: { code: string }) {
  posthogCapture("Coupon Applied", {
    coupon_id: data.code,
  })
}

function mapStep(step: CheckoutStep) {
  switch (step) {
    case CheckoutStep.ShippingInfo:
      return "shipping"
    case CheckoutStep.PaymentInfo:
      return "payment"
    case CheckoutStep.ExpressCheckoutCompletion:
      return "express_summary"
    default:
      return "unknown"
  }
}

// https://posthog.com/docs/data/event-spec/ecommerce-events#checkout-step-viewed
export function posthogTrackCheckoutStepViewed(step: CheckoutStep) {
  posthogCapture("Checkout Step Viewed", {
    step: step,
    shipping_method: getCart()?.shipping_rates.at(0)?.name || null,
    payment_method: getCart()?.payment_methods.at(0) || null,

    // custom
    step_id: mapStep(step),
  })
}

// https://posthog.com/docs/data/event-spec/ecommerce-events#order-completed
export function posthogTrackOrderCompleted(order: TrackingOrder) {
  posthogCapture("Order Completed", mapToPosthogOrder(order))
}

// =====================================
// Custom Events
// https://posthog.com/docs/getting-started/send-events
// =====================================

// Custom events are tracked inline calling `posthogCapture` directly in the code.
